import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { profileService, statusService, statusWorkflowService } from '@/services';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { StatusTransitionDialog } from './StatusTransitionDialog';

interface EntityStatusActionsProps {
  entityType: 'invoice' | 'vendor' | 'price_book' | 'ticket' | 'quote_version';
  currentStatusId: string | null;
  onStatusChange?: (toStatusId: string, toStatusName: string, comments?: string) => void;
  companyId?: string;
}

interface StatusWorkflow {
  id: string;
  transition_label: string;
  tooltip: string | null;
  confirmation_msg: string | null;
  comments: 'no' | 'optional' | 'required';
  seq_num: number;
  to_status: {
    id: string;
    name: string;
  };
}

export function EntityStatusActions({
  entityType,
  currentStatusId,
  onStatusChange,
  companyId: propCompanyId,
}: EntityStatusActionsProps) {
  const { user } = useAuth();
  const [transitionDialog, setTransitionDialog] = useState<{
    open: boolean;
    workflow: StatusWorkflow | null;
  }>({ open: false, workflow: null });

  // Fetch profile to get company_id if not provided
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const result = await profileService.get(user!.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id && !propCompanyId,
  });

  const companyId = propCompanyId || profile?.company_id;

  // Fetch current status by ID
  const { data: currentStatus, isLoading: isLoadingStatus } = useQuery({
    queryKey: ['status', currentStatusId],
    queryFn: async () => {
      const result = await statusService.get(currentStatusId!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!currentStatusId,
  });

  // Fetch available transitions
  const { data: transitions = [], isLoading: isLoadingTransitions } = useQuery({
    queryKey: ['status-workflows', currentStatusId],
    queryFn: async () => {
      const result = await statusWorkflowService.listFromStatus(currentStatusId!);
      if (result.error) throw result.error;
      return result.data as unknown as StatusWorkflow[];
    },
    enabled: !!currentStatusId && !!companyId,
  });

  const handleTransitionClick = (workflow: StatusWorkflow) => {
    // Show dialog if comments needed or confirmation message exists
    if (workflow.comments !== 'no' || workflow.confirmation_msg) {
      setTransitionDialog({ open: true, workflow });
    } else {
      // Transition immediately
      onStatusChange?.(workflow.to_status.id, workflow.to_status.name);
    }
  };

  const handleConfirm = (comments?: string) => {
    if (transitionDialog.workflow) {
      onStatusChange?.(
        transitionDialog.workflow.to_status.id,
        transitionDialog.workflow.to_status.name,
        comments
      );
    }
    setTransitionDialog({ open: false, workflow: null });
  };

  // Loading state
  if (isLoadingStatus || isLoadingTransitions) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-4 w-48" />
        <div className="flex gap-2 mt-4">
          <Skeleton className="h-9 w-28" />
          <Skeleton className="h-9 w-28" />
          <Skeleton className="h-9 w-28" />
        </div>
      </div>
    );
  }

  // No status assigned
  if (!currentStatusId) {
    return (
      <div className="text-sm text-muted-foreground">
        No status assigned
      </div>
    );
  }

  // Status not found
  if (!currentStatus) {
    return (
      <div className="text-sm text-muted-foreground">
        Status not found
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Status Name */}
      <div>
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-medium text-muted-foreground">Status:</span>
          <h3 className="text-lg font-bold text-foreground">
            {currentStatus.name}
          </h3>
        </div>
        {/* Subtext with wrapping */}
        {currentStatus.subtext && (
          <p className="text-sm text-muted-foreground max-w-[280px] leading-tight mt-1">
            {currentStatus.subtext}
          </p>
        )}
      </div>

      {/* Transition Buttons */}
      {transitions.length > 0 && (
        <TooltipProvider>
          <div className="flex flex-wrap gap-2">
            {transitions.map((workflow) => (
              <Tooltip key={workflow.id}>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => handleTransitionClick(workflow)}
                    className="min-w-[100px] max-w-[200px] bg-[hsl(217,72%,46%)] hover:bg-[hsl(217,72%,38%)] text-[hsl(0,0%,100%)] whitespace-normal text-center h-auto py-2"
                  >
                    {workflow.transition_label}
                  </Button>
                </TooltipTrigger>
                {workflow.tooltip && (
                  <TooltipContent>
                    <p>{workflow.tooltip}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            ))}
          </div>
        </TooltipProvider>
      )}

      {/* Transition Dialog */}
      <StatusTransitionDialog
        open={transitionDialog.open}
        onOpenChange={(open) => setTransitionDialog({ open, workflow: open ? transitionDialog.workflow : null })}
        workflow={transitionDialog.workflow}
        onConfirm={handleConfirm}
      />
    </div>
  );
}
